/**
 * StatueScrub — scroll-scrubbed transparent statue.
 *
 * Delivery paths, chosen at runtime:
 *   • VP9 + alpha WebM       → Chrome, Edge, Firefox, Opera, Android Chrome.
 *   • Alpha-packed H.264 MP4 → Safari, iOS (RGB top / greyscale alpha bottom,
 *                              recombined in a small WebGL shader).
 *
 * Assets live in /refactor-artifacts/statue/ (see its README for the
 * format matrix and the make-hevc-alpha.command upgrade path).
 *
 * `progressRef` (optional): a ref whose .current is 0..1. When provided the
 * parent drives playback (the hero scroll region). When absent the component
 * maps its own viewport crossing.
 */
import { useEffect, useRef, useState } from "react";

const BASE = "/refactor-artifacts/statue";
const SIZES = [480, 720, 1440];
const FRAMES = 52;
const FPS = 24;
const AR = 1240 / 1024; /* source aspect, width / height */
const HEVC = false;

const isSafari = () =>
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/Safari/.test(navigator.userAgent) &&
      !/Chrom(e|ium)|Android/.test(navigator.userAgent)));

function pickSize(cssWidth) {
  const c = navigator.connection;
  if (c?.saveData || /(^|\W)(2g|slow-2g)($|\W)/.test(c?.effectiveType || ""))
    return 480;
  /* DPR is clamped at 3, not 2, and phones are no longer capped at 720: a
     3x iPhone needs ~1030 device px for the statue, and since the clip was
     trimmed to 52 frames the 1440 tier costs only ~1.5 MB. Capping it there
     was what made the statue look soft on iOS. */
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const need = cssWidth * dpr;
  return SIZES.find((s) => s >= need) ?? SIZES[SIZES.length - 1];
}

const VS = `attribute vec2 p;varying vec2 uv;
void main(){uv=vec2((p.x+1.)*.5,(1.-p.y)*.5);gl_Position=vec4(p,0.,1.);}`;
/* Output PREMULTIPLIED colour (c*a) into a premultiplied context. Safari has
   long-standing bugs compositing premultipliedAlpha:false canvases, which show
   up as a dark rectangle/halo around the subject. */
const FS = `precision mediump float;varying vec2 uv;uniform sampler2D t;uniform float h;
void main(){vec3 c=texture2D(t,vec2(uv.x,uv.y*h)).rgb;
float a=texture2D(t,vec2(uv.x,uv.y*h+(1.-h))).r;gl_FragColor=vec4(c*a,a);}`;

function initGL(canvas) {
  const gl = canvas.getContext("webgl", {
    premultipliedAlpha: true,
    alpha: true,
    antialias: false,
  });
  if (!gl) return null;
  const sh = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return s;
  };
  const pr = gl.createProgram();
  gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS));
  gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(pr);
  gl.useProgram(pr);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW
  );
  const loc = gl.getAttribLocation(pr, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  return { gl, hLoc: gl.getUniformLocation(pr, "h") };
}

export default function StatueScrub({ className, style, progressRef }) {
  const wrap = useRef(null);
  const video = useRef(null);
  const canvas = useRef(null);
  const glRef = useRef(null);
  const selfProgress = useRef(0);
  const raf = useRef(0);
  const [size, setSize] = useState(null);
  const [src, setSrc] = useState(null);
  const [ready, setReady] = useState(false);
  const packed = isSafari() && !HEVC;

  useEffect(() => {
    const s = pickSize(wrap.current?.clientWidth || 720);
    setSize(s);
    setSrc(
      packed
        ? `${BASE}/statue-${s}.mp4`
        : HEVC && isSafari()
        ? `${BASE}/statue-${s}.hevc.mp4`
        : `${BASE}/statue-${s}.webm`
    );
  }, [packed]);

  /* iOS will not buffer on preload alone — a muted play/pause forces it */
  useEffect(() => {
    const v = video.current;
    if (!v || !src) return undefined;
    const kick = () => {
      const p = v.play();
      if (p) p.then(() => v.pause()).catch(() => {});
    };
    v.addEventListener("loadedmetadata", kick, { once: true });
    const bail = setTimeout(() => setReady((r) => r || v.readyState >= 2), 6000);
    return () => {
      clearTimeout(bail);
      v.removeEventListener("loadedmetadata", kick);
    };
  }, [src]);

  /* self-driven scroll → 0..1 (only when no progressRef supplied) */
  useEffect(() => {
    if (progressRef) return undefined;
    const onScroll = () => {
      const el = wrap.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      selfProgress.current = Math.min(
        1,
        Math.max(
          0,
          (window.innerHeight - r.top) / (window.innerHeight + r.height)
        )
      );
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [progressRef]);

  useEffect(() => {
    if (!packed || !ready) return undefined;
    const onResize = () => sizeCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packed, ready]);

  /* seek + draw loop */
  useEffect(() => {
    if (!ready) return undefined;
    const v = video.current;
    let last = -1;
    const tick = () => {
      const p = progressRef ? progressRef.current : selfProgress.current;
      const frame = Math.round(Math.min(1, Math.max(0, p)) * (FRAMES - 1));
      if (frame !== last) {
        last = frame;
        const t = (frame + 0.5) / FPS;
        if (v.fastSeek) v.fastSeek(t);
        else v.currentTime = t;
      }
      if (packed && glRef.current) {
        const { gl, hLoc } = glRef.current;
        const H = v.videoHeight;
        const half = (H - 2) / 2;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, v);
        gl.uniform1f(hLoc, half / H);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [ready, packed, progressRef]);

  /* Match the canvas backing store to how large it is actually drawn, capped
     at the source resolution. Fixing it at videoWidth meant a 720-wide buffer
     was being stretched across ~1030 device px on a 3x phone. */
  const sizeCanvas = () => {
    const v = video.current;
    const c = canvas.current;
    if (!v || !c || !v.videoWidth) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const cssW = wrap.current?.clientWidth || v.videoWidth;
    const w = Math.max(1, Math.min(Math.round(cssW * dpr), v.videoWidth));
    const h = Math.max(1, Math.round((w * (v.videoHeight - 2)) / 2 / v.videoWidth));
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
      glRef.current?.gl.viewport(0, 0, w, h);
    }
  };

  const onLoaded = () => {
    if (packed && canvas.current && !glRef.current) {
      glRef.current = initGL(canvas.current);
      sizeCanvas();
    }
    setReady(true);
  };

  const media = {
    width: "100%",
    height: "auto",
    aspectRatio: AR,
    display: "block",
  };

  return (
    <div
      ref={wrap}
      className={className}
      style={{ position: "relative", ...style }}
      aria-hidden="true"
    >
      <img
        src={size ? `${BASE}/poster-${size}.webp` : undefined}
        alt=""
        aria-hidden
        style={{
          ...media,
          position: "absolute",
          inset: 0,
          opacity: ready ? 0 : 1,
          transition: "opacity .25s ease",
        }}
      />
      <video
        ref={video}
        src={src || undefined}
        muted
        playsInline
        preload="auto"
        onCanPlayThrough={onLoaded}
        onLoadedData={packed ? onLoaded : undefined}
        style={
          packed
            ? {
                /* Full-size but invisible behind the canvas. iOS sizes a
                   video's decode surface to its layout box, so a 1x1 element
                   can hand texImage2D a downscaled frame — that was the
                   pixelation. */
                ...media,
                position: "absolute",
                inset: 0,
                opacity: 0,
                pointerEvents: "none",
              }
            : { ...media, opacity: ready ? 1 : 0 }
        }
      />
      {packed && (
        <canvas
          ref={canvas}
          style={{ ...media, opacity: ready ? 1 : 0 }}
        />
      )}
    </div>
  );
}
